import { createContext, useCallback, useMemo, useState } from 'react';
import type { Context, Dispatch, ReactNode, SetStateAction } from 'react';

import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT, NULL_FUNCTION } from '@e-socna/empty';

import { FormVariant } from '../../../../forms';
import { emptyMemosState } from '../../../../forms/MemoBeforeSubmit';
import type { MemosState } from '../../../../forms/MemoBeforeSubmit';

import type { WizardProps } from '../../types/props.type';
import type { WizardError } from '../../types/error.type';

export interface WizardData<V extends object, VS extends object, SE> {
  readonly isSubmitLocked: boolean;
  readonly setIsSubmitLocked: Dispatch<SetStateAction<boolean>>;
  readonly currentStepId: SE;
  readonly setCurrentStepId: Dispatch<SetStateAction<SE>>;
  readonly snapshot: V;
  readonly setSnapshot: Dispatch<SetStateAction<V>>;
  readonly wizardError: WizardError<SE> | undefined;
  readonly setWizardError: Dispatch<
    SetStateAction<WizardError<SE> | undefined>
  >;
  readonly props: WizardProps<V, VS, SE>;
  readonly memos: MemosState;
  readonly setMemos: Dispatch<SetStateAction<MemosState>>;
  readonly clearMemos: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WizardContext = createContext<WizardData<object, object, any>>({
  isSubmitLocked: false,
  setIsSubmitLocked: NULL_FUNCTION,
  currentStepId: 'step',
  setCurrentStepId: NULL_FUNCTION,
  snapshot: EMPTY_OBJECT,
  setSnapshot: NULL_FUNCTION,
  wizardError: undefined,
  setWizardError: NULL_FUNCTION,
  props: {
    askMemoBeforeSubmit: false,
    formVariant: FormVariant.Creation,
    onSubmit: () => undefined,
    steps: EMPTY_ARRAY,
    wizardChildren: false,
  },
  memos: emptyMemosState,
  setMemos: NULL_FUNCTION,
  clearMemos: NULL_FUNCTION,
});

type Props<V extends object, VS extends object, SE> = WizardProps<V, VS, SE> &
  Readonly<{
    children: ReactNode;
  }>;

const WizardContextProvider = <V extends object, VS extends object, SE>({
  children,
  ...props
}: Readonly<Props<V, VS, SE>>) => {
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);

  // NB : can't use useSteps hook here since they depend mutually on each other, so we have to do it manually
  const { firstStepId } = ensureDefined<{ firstStepId: SE }>({
    firstStepId: props.steps.find((step) => step.isEnabled)?.id,
  });

  const [currentStepId, setCurrentStepId] = useState(firstStepId);

  // NB : can't use useConsolidation hook here since they depend mutually on each other, so we have to do it manually
  const consolidatedInitialValues = props.steps.reduce(
    (acc, step) => ({ ...acc, ...(step.initialValues ?? EMPTY_OBJECT) }),
    props.initialValues ?? (EMPTY_OBJECT as V),
  );

  const [snapshot, setSnapshot] = useState(consolidatedInitialValues);

  const [wizardError, setWizardError] = useState<WizardError<SE>>();

  const [memos, setMemos] = useState(emptyMemosState);

  const clearMemos = useCallback(() => {
    setMemos(emptyMemosState);
  }, []);

  const value = useMemo(
    () => ({
      isSubmitLocked,
      setIsSubmitLocked,
      currentStepId,
      setCurrentStepId,
      snapshot,
      setSnapshot,
      props: {
        ValidationSchema: undefined,
        consolidationMode: 'onlyCurrent' as const,
        formTitle: undefined,
        initialValues: undefined,
        onCancel: undefined,
        setMustBlockAction: undefined,
        showCancelButton: true,
        validate: undefined,
        ...props,
      },
      wizardError,
      setWizardError,
      memos,
      setMemos,
      clearMemos,
    }),
    [
      isSubmitLocked,
      setIsSubmitLocked,
      setCurrentStepId,
      currentStepId,
      snapshot,
      props,
      wizardError,
      memos,
      clearMemos,
    ],
  );

  const WC = WizardContext as unknown as Context<WizardData<V, VS, SE>>;

  return <WC.Provider value={value}>{children}</WC.Provider>;
};

export { WizardContext, WizardContextProvider };
